// AxiosContext.js – fast init, single refresh queue, proactive refresh
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import jwtDecode from "jwt-decode";
import awsconfig from "../config/awsconfig";
import { logoutSuccess, setRefreshedToken } from "../store/actions";

const client_id = awsconfig.REACT_APP_REST_CLIENT_ID;
const apiUrl = awsconfig.REACT_APP_PUBLIC_API;
const defaultTimeoutMs = Number(awsconfig.REACT_APP_API_TIMEOUT_MS || 60000);
const TOKEN_KEY = `swobbee.portal.${client_id}.token`;
const REFRESH_KEY = `swobbee.portal.${client_id}.refresh_token`;
const EXPIRY_KEY = `swobbee.portal.${client_id}.tokenExpiry`;

const getStoredToken = () => localStorage.getItem(TOKEN_KEY) || null;

const isTokenExpiringSoon = (expirySeconds, bufferSeconds = 300) => {
  if (!expirySeconds) return true;
  return Date.now() / 1000 > Number(expirySeconds) - bufferSeconds;
};

export const clearSessionStorage = () => {
  const prefix = `swobbee.portal.${client_id}`;
  [
    "name",
    "token",
    "userName",
    "displayName",
    "tokenExpiry",
    "refresh_token",
    "idToken",
    "user_group",
  ].forEach((key) => localStorage.removeItem(`${prefix}.${key}`));
  localStorage.removeItem("client_id");
};

export const AxiosContext = createContext();

export const AxiosProvider = ({ children }) => {
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [accessToken, setAccessToken] = useState(getStoredToken);
  const tokenRef = useRef(accessToken);
  const setAccessTokenRef = useRef(null);

  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  const clearAuthAndLogout = useCallback(() => {
    clearSessionStorage();
    setAccessToken(null);
    if (dispatchRef.current) dispatchRef.current(logoutSuccess());
  }, []);

  const axiosInstance = useMemo(() => {
    if (!accessToken) return null;

    const instance = axios.create({
      baseURL: apiUrl,
      timeout: defaultTimeoutMs,
      headers: {
        client_id,
        "Content-Type": "application/json",
      },
    });

    instance.interceptors.request.use((config) => {
      config.headers.access_token = tokenRef.current || accessToken;
      config.headers.Authorization = tokenRef.current || accessToken;
      config.headers.client_id = client_id;

      return config;
    });

    const MAX_RETRIES = 2;
    const RETRY_DELAY = 800;
    let isRefreshing = false;
    let refreshSubscribers = [];

    const subscribeTokenRefresh = (callback) => {
      refreshSubscribers.push(callback);
    };

    const onRefreshed = (newToken) => {
      refreshSubscribers.forEach((cb) => cb(newToken));
      refreshSubscribers = [];
    };

    const refreshAccessToken = async () => {
      const refresh_token = localStorage.getItem(REFRESH_KEY);
      if (!refresh_token) return null;
      try {
        const { data } = await axios.post(`${apiUrl}/refresh_token`, {
          refresh_token,
          client_id,
        });
        const newToken = data.data?.access_token;
        if (newToken) {
          localStorage.setItem(TOKEN_KEY, newToken);
          tokenRef.current = newToken;
          try {
            const decoded = jwtDecode(newToken);
            if (decoded.exp != null) {
              localStorage.setItem(EXPIRY_KEY, String(decoded.exp));
              if (dispatchRef.current) {
                dispatchRef.current(
                  setRefreshedToken({
                    token: newToken,
                    tokenExpiry: decoded.exp,
                  })
                );
              }
            }
          } catch (_) {}
          if (setAccessTokenRef.current) {
            setAccessTokenRef.current(newToken);
          }
        }
        return newToken;
      } catch (err) {
        console.error("Refresh token error:", err);
        clearAuthAndLogout();
        return null;
      }
    };

    const requestWithRetry = async (config) => {
      config._retryCount = config._retryCount || 0;

      while (config._retryCount < MAX_RETRIES) {
        try {
          config._retryCount += 1;
          const response = await instance.request(config);
          return response;
        } catch (error) {
          const refresh_token = localStorage.getItem(REFRESH_KEY);
          const isTimeout =
            error.code === "ECONNABORTED" ||
            error.message?.includes("timeout") ||
            error.response?.status === 504 ||
            !error.response;

          if (isTimeout && config._retryCount < MAX_RETRIES) {
            await new Promise((r) =>
              setTimeout(r, RETRY_DELAY * config._retryCount),
            );
            continue;
          }

          if (error.response?.status === 401 && !config._isRetry) {
            config._isRetry = true;
            if (!refresh_token) {
              clearAuthAndLogout();
              throw error;
            }

            if (!isRefreshing) {
              isRefreshing = true;
              try {
                const newToken = await refreshAccessToken();
                if (newToken) {
                  onRefreshed(newToken);
                } else {
                  clearAuthAndLogout();
                  onRefreshed(null);
                }
              } finally {
                isRefreshing = false;
              }
            }

            return new Promise((resolve, reject) => {
              subscribeTokenRefresh((newToken) => {
                if (!newToken) {
                  reject(error);
                  return;
                }
                config.headers.access_token = newToken;
                config.headers.Authorization = newToken;
                config.headers.client_id = client_id;
                resolve(requestWithRetry(config));
              });
            });
          }

          throw error;
        }
      }

      throw new Error("Request failed after maximum retries");
    };

    const wrappedInstance = new Proxy(instance, {
      get(target, prop) {
        if (
          ["request", "get", "post", "put", "delete", "patch"].includes(prop)
        ) {
          return (url, dataOrConfig = {}, maybeConfig = {}) => {
            let config = {};
            if (["post", "put", "patch"].includes(prop)) {
              config = {
                ...maybeConfig,
                url,
                method: prop,
                data: dataOrConfig,
              };
            } else {
              config = { ...dataOrConfig, url, method: prop };
            }
            if (!config.headers) config.headers = {};
            config.headers["Content-Type"] =
              config.headers["Content-Type"] || "application/json";
            return requestWithRetry(config);
          };
        }
        return target[prop];
      },
    });

    return wrappedInstance;
  }, [accessToken]);

  const setAccessTokenAndStorage = useCallback((token) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setAccessToken(token);
  }, []);

  useEffect(() => {
    setAccessTokenRef.current = setAccessTokenAndStorage;
    return () => {
      setAccessTokenRef.current = null;
    };
  }, [setAccessTokenAndStorage]);

  useEffect(() => {
    if (!accessToken) return;
    const expiry = localStorage.getItem(EXPIRY_KEY);
    if (!isTokenExpiringSoon(expiry)) return;

    const refresh_token = localStorage.getItem(REFRESH_KEY);
    if (!refresh_token) return;

    axios
      .post(`${apiUrl}/refresh_token`, { refresh_token, client_id })
      .then(({ data }) => {
        const newToken = data.data?.access_token;
        if (newToken) {
          localStorage.setItem(TOKEN_KEY, newToken);
          tokenRef.current = newToken;
          try {
            const decoded = jwtDecode(newToken);
            if (decoded.exp != null) {
              localStorage.setItem(EXPIRY_KEY, String(decoded.exp));
              if (dispatchRef.current) {
                dispatchRef.current(
                  setRefreshedToken({
                    token: newToken,
                    tokenExpiry: decoded.exp,
                  })
                );
              }
            }
          } catch (_) {}
          setAccessTokenAndStorage(newToken);
        }
      })
      .catch(() => {});
  }, [accessToken, setAccessTokenAndStorage]);

  return (
    <AxiosContext.Provider
      value={{
        axiosInstance,
        setAccessToken: setAccessTokenAndStorage,
        accessToken,
      }}
    >
      {children}
    </AxiosContext.Provider>
  );
};
